import React, { useState, useContext, useEffect } from 'react';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import {
  LayoutManager,
  LayoutGroup,
} from '@thd-olt-component-react/layout-manager';
import {
  oneOfType, arrayOf, node, bool, string, number
} from 'prop-types';
import { Heading, Carousel } from '@thd-olt-component-react/core-ui';
import { getSponsoredBanner } from '@thd-olt-component-react/sponsored-content';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classnames from 'classnames/bind';
import { isGuest } from '../utils/index';
import { MarketingOffers } from './MarketingOffers';

import componentStyles from './section.module.scss';

const styles = classnames.bind(componentStyles);

/* eslint-disable */
export function CustomRenderedSection({
  SCROLL_BUFFER,
  PLACEHOLDER_SECTION,
  mcvisID,
  store,
}) {
  return function Section(props) {
    if (!props.children || props.children.length === 0) return null;
    const { customRenderId, id } = props;

    switch (customRenderId) {
      case 'THD Capabilities':
        delete props.title;
        return (
          <div
            key={id}
            className={styles(
              'component__container',
              'component__container--light',
              'component__container--light-items'
            )}
          >
            <SectionComponent {...props} />
          </div>
        );
      case 'Marketing Gallery':
        // The purpose of this case stmt is for the marketing offers a/b test
        // See https://confluence.homedepot.com/x/AgYYFQ
        delete props.title;
        const { storeId } = store;

        return (
          <Hydrator
            id={`${props.id}-marketing-gallery-php-hydrator`}
            className={styles('u-height-100')}
            scrollBuffer={SCROLL_BUFFER}
            placeholder={PLACEHOLDER_SECTION}
          >
            <LayoutManager name="marketing-offers">
              <LayoutGroup name="offers-test">
                <MarketingOffers
                  key={props.id}
                  mcvisID={mcvisID}
                  storeId={storeId}
                  {...props}
                />
              </LayoutGroup>
            </LayoutManager>
          </Hydrator>
        );
      default:
        delete props.title;

        const isHeroCarousel = customRenderId === 'Hero Carousel';

        if (isHeroCarousel) {
          return <SectionComponent key={props.id} {...props} autoplay loop />;
        }

        return (
          <Hydrator
            id={`${props.id}-sectionHydrators-php-hydrator`}
            className={styles('u-height-100')}
            scrollBuffer={SCROLL_BUFFER}
            placeholder={PLACEHOLDER_SECTION}
          >
            <SectionComponent key={props.id} {...props} />
          </Hydrator>
        );
    }
  };
}

function SectionComponent({
  autoplay,
  children,
  isCarousel,
  title,
  slidesSm,
  slidesMd,
  slidesLg,
  slidesXl,
  pagination,
  loop,
  customRenderId,
}) {
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  let initialCarouselItems = [];
  for (let i = 0; i < children.length; i += 1) {
    if (isCarousel) {
      initialCarouselItems.push(
        <div key={`item-${i}`} className={styles('section__carousel')}>
          {children[i]}
        </div>
      );
    }
  }

  const [carouselItems, setCarouselItems] = useState(initialCarouselItems);
  const [gotSponsored, setGotSponsored] = useState(false);
  const browserId = experienceContext?.cookie?.adobeCookie?.MCMID;

  async function checkForSponsoredBanner() {
    const updatedCarouselItems = await getSponsoredBanner({
      newCarousel: initialCarouselItems,
      channel,
      cx: styles,
      classNames: 'section__carousel',
      isGuest: isGuest(),
      experienceContext,
    });

    setGotSponsored(true);
    setCarouselItems(updatedCarouselItems);
  }

  const checkSponsoredBannerAsync = async () => {
    await checkForSponsoredBanner();
  };

  useEffect(() => {
    if (customRenderId === 'Hero Carousel' && !gotSponsored && browserId) {
      checkSponsoredBannerAsync();
    }
  }, [customRenderId, browserId, gotSponsored]);

  if (carouselItems.length > 0) {
    return (
      <>
        {title && (
          <Heading
            title={title}
            className={styles('section__carousel--title')}
            underline
          />
        )}
        <Carousel
          loop={loop}
          autoplay={autoplay}
          peek={false}
          pagination={pagination}
          slidesPerGroup={1}
          slidesPerView={1}
          breakpoints={{
            sm: {
              slidesPerView: slidesSm || 1,
              slidesPerGroup: 1,
            },
            md: {
              slidesPerView: slidesMd || 1,
              slidesPerGroup: 1,
            },
            lg: {
              slidesPerView: slidesLg || 1,
              slidesPerGroup: 1,
            },
            xl: {
              slidesPerView: slidesXl || 1,
              slidesPerGroup: 1,
            },
          }}
        >
          {carouselItems}
        </Carousel>
      </>
    );
  }
  return (
    <>
      {title && (
        <Heading title={title} className={styles('section--title')} underline />
      )}
      <div className={styles('section__container')}>{children}</div>
    </>
  );
}

SectionComponent.propTypes = {
  autoplay: bool,
  children: oneOfType([node, arrayOf(node)]),
  isCarousel: bool,
  title: string,
  slidesSm: number,
  slidesMd: number,
  slidesLg: number,
  slidesXl: number,
  pagination: bool,
  loop: bool,
  customRenderId: string,
};

SectionComponent.defaultProps = {
  autoplay: false,
  children: null,
  isCarousel: false,
  title: null,
  slidesSm: null,
  slidesMd: null,
  slidesLg: null,
  slidesXl: null,
  pagination: true,
  loop: false,
  customRenderId: null,
};
